<script setup>
import { ref, computed, provide, onMounted } from 'vue'
import { useRequest } from 'composables/useRequest'
import { useFormatting } from 'composables/useFormatting'
import HostPanel from './HostPanel.vue'
import CalendarPanel from './CalendarPanel.vue'
import LoadingSkeleton from './LoadingSkeleton.vue'
import BookingForm from './BookingForm.vue'

const { axiosRequest } = useRequest()
const { DaysFromNowOn } = useFormatting()

// Reschedule mode: data injected by ERB when visiting /bookings/:id/reschedule.
const rescheduleData = window.RESCHEDULE_DATA ?? null
const isRescheduleMode = !!rescheduleData

// Parse user_url and event_url — from reschedule data or from the URL.
const pathParts = window.location.pathname.split('/').filter(Boolean)
const isEmbedded = !isRescheduleMode && pathParts[0] === 'embed'
const userUrl  = isRescheduleMode ? rescheduleData.user_url  : (isEmbedded ? pathParts[1] : pathParts[0])
const eventUrl = isRescheduleMode ? rescheduleData.event_url : (isEmbedded ? pathParts[2] : pathParts[1])

// Page state
const loading = ref(true)
const error   = ref(null)
const pageData = ref(null)

// Shared mutable state — provided to all children
const attendee     = ref({ full_name: '', email: '', timezone: null, time_format: '12_hours' })
const selectedSlot = ref(null)
const step         = ref('calendar') // 'calendar' | 'form'

// Derived display data from API response
const event          = computed(() => pageData.value?.event          ?? null)
const branding       = computed(() => pageData.value?.branding       ?? {})
const customTemplate = computed(() => pageData.value?.custom_template ?? {})
const socialLibrary  = computed(() => pageData.value?.social_library  ?? {})
const profilePicture = computed(() => pageData.value?.profile_picture ?? null)
const profilePath    = computed(() => pageData.value?.profile_path    ?? '/')
const timezones      = computed(() => pageData.value?.time_zones      ?? [])
const guestEmails    = computed(() => pageData.value?.guest_emails    ?? [])

// Provide everything children may need (computed refs stay reactive through inject)
provide('event',          event)
provide('attendee',       attendee)
provide('step',           step)
provide('selectedSlot',   selectedSlot)
provide('branding',       branding)
provide('customTemplate', customTemplate)
provide('socialLibrary',  socialLibrary)
provide('profilePicture', profilePicture)
provide('profilePath',    profilePath)
provide('timezones',      timezones)
provide('guestEmails',    guestEmails)
provide('isEmbedded',      isEmbedded)
provide('userUrl',         userUrl)
provide('eventUrl',        eventUrl)
provide('rescheduleData',  rescheduleData)

const runProcess = new URLSearchParams(window.location.search).get('run_process') === 'true'

async function fetchPageData() {
  loading.value = true
  error.value   = null
  const apiUrl = runProcess
    ? `/api/${userUrl}/${eventUrl}/page_data?run_process=true`
    : `/api/${userUrl}/${eventUrl}/page_data`
  const res = await axiosRequest('GET', apiUrl, {}, false)
  if (!res.js_error && res.response.success) {
    pageData.value = res.response.data
    initAttendee()
  } else {
    error.value = res.js_error
      ? 'Something went wrong. Please try again.'
      : (res.response?.message ?? 'Event not found.')
  }
  loading.value = false
}

function initAttendee() {
  const tz = timezones.value

  if (isRescheduleMode) {
    attendee.value.full_name = rescheduleData.attendee_name  ?? ''
    attendee.value.email     = rescheduleData.attendee_email ?? ''
    const zone = tz.find(z => z.name === rescheduleData.attendee_timezone)
    attendee.value.timezone  = zone ?? tz.find(z => z.name === 'UTC') ?? tz[0]
    return
  }

  const ca = pageData.value?.current_attendee
  if (ca) {
    attendee.value.full_name   = ca.full_name   ?? ''
    attendee.value.email       = ca.email       ?? ''
    attendee.value.time_format = ca.time_format ?? '12_hours'
    attendee.value.timezone    = tz.find(z => z.name === ca.timezone) ?? tz[0]
  } else {
    try {
      const rawTz = Intl.DateTimeFormat().resolvedOptions().timeZone
      // Try exact match, then each path segment (handles America/Indiana/Indianapolis)
      let zone = tz.find(z => z.name === rawTz)
      if (!zone) {
        const parts = rawTz.split('/')
        for (let i = parts.length - 1; i >= 0 && !zone; i--) {
          const city = parts[i].replace(/_/g, ' ')
          zone = tz.find(z => z.name.toLowerCase().includes(city.toLowerCase()))
        }
      }
      attendee.value.timezone = zone ?? tz.find(z => z.name === 'UTC') ?? tz[0]
    } catch {
      attendee.value.timezone = tz.find(z => z.name === 'UTC') ?? tz[0]
    }
  }
}

function handleSlotConfirmed(slot) {
  selectedSlot.value = slot
  step.value = 'form'
}

onMounted(() => {
  fetchPageData()
  // Clear so it doesn't bleed into other pages on Turbo navigation
  if (isRescheduleMode) delete window.RESCHEDULE_DATA
})
</script>

<template>
  <div :class="isEmbedded ? 'h-screen' : 'md:h-[99.99%]'">
    <div class="md:h-[99.99%]">
      <section class="flex justify-center items-center md:p-4 w-full md:overflow-hidden md:h-[99.99%]">

        <!-- Loading skeleton -->
        <LoadingSkeleton v-if="loading" />

        <!-- Error state -->
        <div v-else-if="error"
             class="flex flex-col items-center justify-center py-32 text-center px-4 w-full">
          <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 9v2m0 4h.01M12 3a9 9 0 110 18A9 9 0 0112 3z" />
            </svg>
          </div>
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-5">{{ error }}</p>
          <button @click="fetchPageData"
                  class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
            Try again
          </button>
        </div>

        <!-- Main card -->
        <div v-else
             id="timelydo_public_event_card"
             class="flex-grow bg-white md:h-[99.99%] overflow-hidden md:rounded-lg transition-all duration-500 md:shadow-lg shadow-gray-300 dark:shadow-gray-600/40 dark:bg-[#1C2128] dark:border-gray-500">
          <div class="w-full md:flex md:h-full">

            <!-- Left panel: always visible — border matches profile page exactly -->
            <HostPanel class="border-b pb-5 w-full md:w-2/5 flex flex-col h-full md:h-[99.9%]
                              flex-grow md:border-b-0 md:border-r border-gray-200 dark:border-gray-500" />

            <!-- Calendar panel: hidden on mobile when form is shown -->
            <CalendarPanel
              :class="{ 'hidden': step === 'form' }"
              class="flex w-full md:w-3/5 col-span-2 h-full md:h-[99.9%]"
              @slot-confirmed="handleSlotConfirmed"
            />

            <BookingForm
              v-if="step === 'form'"
              class="sm:w-3/5 mx-auto col-span-2 overflow-y-auto scrollbar-style
                     px-0 sm:px-4 md:px-8 py-6"
              @back="step = 'calendar'"
            />

          </div>
        </div>

      </section>
    </div>
  </div>
</template>
